import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Layout } from "@/components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { WelcomeOfferStatus } from "@/components/WelcomeOfferStatus"
import { useUserDetail } from "@/hooks/useUserDetail"
import { useUpdateWelcomeOfferStatus, type AdminWelcomeOfferStatus } from "@/hooks/useUsers"
import { useToast } from "@/components/Toast"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useCreateTeam } from "@/hooks/useTeams"
import { createTeamToast } from "@/lib/createTeamResult"
import { APIError } from "@/api"

function timestamp(value: string | null | undefined) {
  if (!value) return "—"
  return new Date(
    value.includes("T") || value.endsWith("Z") ? value : value.replace(" ", "T") + "Z"
  ).toLocaleString()
}

export function WelcomeOfferDetail() {
  const { userId = "" } = useParams()
  const { data, isPending, isError, refetch } = useUserDetail(userId)
  const update = useUpdateWelcomeOfferStatus()
  const { toast } = useToast()
  const createTeam = useCreateTeam()
  const queryClient = useQueryClient()
  const [confirmingCreate, setConfirmingCreate] = useState(false)
  const offer = data?.welcome_offer
  const submittedTeamName = offer?.setup_team_name?.trim() ?? ""
  const teamAlreadyExists = !!(
    submittedTeamName &&
    data?.teams.some((team) => team.name.trim().toLowerCase() === submittedTeamName.toLowerCase())
  )
  /** Provisioning is refused server-side for a closed request (onboarded/cold), so do not offer
   * an action that cannot succeed — the Reopen button in this page is the way back. */
  const canProvision = !!offer && ["accepted", "emailed"].includes(offer.status)
  const createAndComp = async () => {
    if (!data || !offer) return
    try {
      // No `comp` is sent: the server derives this offer's grant from `welcomeOfferUserPublicId`.
      const result = await createTeam.mutateAsync({
        name: submittedTeamName,
        creatorUserPublicId: userId,
        level: offer.setup_level?.trim() || undefined,
        league: offer.setup_league?.trim() || undefined,
        type: "football",
        welcomeOfferUserPublicId: userId,
      })
      toast(createTeamToast(result, result.team.name))
      void queryClient.invalidateQueries({ queryKey: ["user", userId] })
    } catch (e) {
      toast({
        title: "Error creating team",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }
  const changeStatus = (status: AdminWelcomeOfferStatus) =>
    update.mutate(
      { userId, status },
      {
        onSuccess: () =>
          toast({
            title:
              status === "onboarded"
                ? "Marked onboarded"
                : status === "cold"
                  ? "Marked cold"
                  : "Request reopened",
          }),
      }
    )
  return (
    <Layout>
      <div className="mx-auto max-w-5xl space-y-6">
        <Link to="/offers/diagrams" className="text-sm text-blue-600 hover:underline">
          ← Diagrams welcome offer
        </Link>
        {isPending ? (
          <p role="status">Loading request…</p>
        ) : isError ? (
          <div role="alert">
            <p>Could not load this request.</p>
            <Button onClick={() => void refetch()}>Try again</Button>
          </div>
        ) : !offer || !data || ["shown", "dismissed"].includes(offer.stage) ? (
          <p>This account has no claimed welcome-offer request.</p>
        ) : (
          <>
            <PageHeader
              title={data.user.name || data.user.email}
              description={data.user.email}
              actions={<WelcomeOfferStatus stage={offer.stage} />}
            />
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-6">
                <section className="rounded-xl border border-border bg-surface p-6">
                  <h2 className="text-lg font-semibold">Team details</h2>
                  {offer.setup_submitted_at ? (
                    <>
                      <dl className="mt-5 grid grid-cols-[140px_minmax(0,1fr)] gap-x-4 gap-y-4 text-sm">
                        <dt className="text-secondary">Team name</dt>
                        <dd className="break-words font-medium">{offer.setup_team_name}</dd>
                        <dt className="text-secondary">Level</dt>
                        <dd>{offer.setup_level}</dd>
                        <dt className="text-secondary">League / conference</dt>
                        <dd>{offer.setup_league || "Not provided"}</dd>
                      </dl>
                      <p className="mt-6 border-t border-border pt-4 text-xs text-secondary">
                        Received {timestamp(offer.setup_submitted_at)}
                      </p>
                    </>
                  ) : (
                    <p className="mt-4 text-sm text-secondary">
                      This coach hasn&apos;t submitted team details yet.
                    </p>
                  )}
                </section>
                <section className="rounded-xl border border-border bg-surface p-6">
                  <h2 className="text-lg font-semibold">Follow up</h2>
                  <p className="mt-2 text-sm text-secondary">
                    Review the request, reach out to the coach, and set up their team with a free
                    year on the Team plan. Mark onboarded once they have their sign-in instructions.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-4 text-sm font-medium">
                    <Link
                      to="/email/compose"
                      state={{ to: data.user.email, subject: "Your free year of Playside film" }}
                      className="text-blue-600 hover:underline"
                    >
                      Write email
                    </Link>
                    <Link to="/teams" className="text-blue-600 hover:underline">
                      Manage teams
                    </Link>
                    <Link to={`/users/${userId}`} className="text-blue-600 hover:underline">
                      View account
                    </Link>
                  </div>
                  {offer.setup_submitted_at && canProvision && (
                    <div className="mt-5 border-t border-border pt-4">
                      <Button
                        disabled={createTeam.isPending || teamAlreadyExists}
                        onClick={() => setConfirmingCreate(true)}
                      >
                        {createTeam.isPending ? "Creating team…" : "Create team and comp a year"}
                      </Button>
                      <p className="mt-2 text-xs text-secondary">
                        {teamAlreadyExists
                          ? `They already have a team named "${submittedTeamName}".`
                          : `Creates "${submittedTeamName}" from their details, makes them its admin, and comps the Team plan for one year.`}
                      </p>
                    </div>
                  )}
                  {offer.setup_submitted_at && !canProvision && (
                    <p className="mt-5 border-t border-border pt-4 text-xs text-secondary">
                      This request is {offer.status}. Reopen it to provision a team.
                    </p>
                  )}
                  {data.teams.length > 0 && (
                    <div className="mt-5 border-t border-border pt-4">
                      <h3 className="text-sm font-medium">Existing teams</h3>
                      <ul className="mt-2 space-y-1 text-sm">
                        {data.teams.map((team) => (
                          <li key={team.public_id}>
                            <Link
                              to={`/teams/${team.public_id}`}
                              className="text-blue-600 hover:underline"
                            >
                              {team.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {["accepted", "emailed", "onboarded", "cold"].includes(offer.status) && (
                    <div className="mt-6 border-t border-border pt-5">
                      {offer.stage === "cold" || offer.stage === "onboarded" ? (
                        !offer.setup_declined_at && (
                          <Button
                            variant="outline"
                            disabled={update.isPending}
                            onClick={() => changeStatus("accepted")}
                          >
                            Reopen request
                          </Button>
                        )
                      ) : (
                        <div className="flex gap-3">
                          <Button
                            disabled={update.isPending}
                            onClick={() => changeStatus("onboarded")}
                          >
                            Mark onboarded
                          </Button>
                          <Button
                            variant="outline"
                            disabled={update.isPending}
                            onClick={() => changeStatus("cold")}
                          >
                            Mark cold
                          </Button>
                        </div>
                      )}
                      <p className="mt-3 text-xs text-secondary">
                        Marking cold stops reminders and releases the reserved spot.
                      </p>
                    </div>
                  )}
                  {update.isError && (
                    <p role="alert" className="mt-3 text-sm text-red-600">
                      Could not update this request. Please try again.
                    </p>
                  )}
                </section>
              </div>
              <section className="self-start rounded-xl border border-border bg-surface p-6">
                <h2 className="text-lg font-semibold">Activity</h2>
                <dl className="mt-5 space-y-5 text-sm">
                  <div>
                    <dt className="font-medium">Offer accepted</dt>
                    <dd className="mt-1 text-secondary">{timestamp(offer.responded_at)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium">Team details received</dt>
                    <dd className="mt-1 text-secondary">
                      {offer.setup_submitted_at
                        ? timestamp(offer.setup_submitted_at)
                        : "Waiting for coach"}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Setup email</dt>
                    <dd className="mt-1 text-secondary">
                      {offer.setup_email_sent_at
                        ? timestamp(offer.setup_email_sent_at)
                        : "Not sent automatically"}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Reminder</dt>
                    <dd className="mt-1 text-secondary">
                      {offer.setup_reminder_sent_at
                        ? timestamp(offer.setup_reminder_sent_at)
                        : "Not sent"}
                    </dd>
                  </div>
                  {offer.setup_declined_at && (
                    <div>
                      <dt className="font-medium">Coach declined</dt>
                      <dd className="mt-1 text-secondary">{timestamp(offer.setup_declined_at)}</dd>
                    </div>
                  )}
                </dl>
                {(offer.setup_email_attempts ?? 0) >= 3 && !offer.setup_email_due_at && (
                  <p className="mt-5 text-sm text-amber-600">
                    Automatic email attempts exhausted. Check the email log before following up.
                  </p>
                )}
                <Link
                  to="/email/log"
                  className="mt-6 inline-block text-sm font-medium text-blue-600 hover:underline"
                >
                  Open email log
                </Link>
              </section>
            </div>
            <ConfirmDialog
              open={confirmingCreate}
              onOpenChange={setConfirmingCreate}
              title={`Create "${submittedTeamName}" and comp a year?`}
              description={`Creates the team, adds ${data.user.email} as its admin, and starts a real Stripe subscription for the Team plan at 100% off, ending one year from today. Undoing this means revoking the comp in Stripe.`}
              confirmLabel="Create team and comp"
              onConfirm={() => {
                setConfirmingCreate(false)
                void createAndComp()
              }}
            />
          </>
        )}
      </div>
    </Layout>
  )
}
